import type { AuthClient } from "@nav-games/auth-client";

interface PlayProps {
  auth: AuthClient;
  onNavigate: (to: string) => void;
}

interface ModuleEntry {
  slug: string;
  name: string;
  description: string;
  ready: boolean;
}

const MODULES: readonly ModuleEntry[] = [
  {
    slug: "morse",
    name: "Morse code",
    description: "ITU letter and number tables. Encode, listen, decode by ear, decode by mic.",
    ready: true,
  },
  {
    slug: "semaphore",
    name: "Semaphore",
    description: "Eight clock-face arm positions. A–Z.",
    ready: true,
  },
  {
    slug: "flags",
    name: "Maritime signal flags",
    description: "ICS letter flags (A–Z) and numeric pennants (0–9). Each carries a meaning when flown alone.",
    ready: true,
  },
  {
    slug: "lights",
    name: "Navigation lights",
    description: "IRPCS Part C light catalogue. Identify ships at night by their lights.",
    ready: true,
  },
  {
    slug: "weather",
    name: "Met Office weather",
    description: "Wind force, visibility, sea state and pressure terminology used in UK Shipping + Inshore Waters forecasts.",
    ready: true,
  },
  {
    slug: "distress",
    name: "Distress signals",
    description: "IRPCS Annex IV — non-flag distress signals: flares, orange smoke, EPIRB, SART, arms-wave, dye marker.",
    ready: true,
  },
  {
    slug: "mayday",
    name: "Mayday + NATO phonetic",
    description: "Build a Channel-16 distress transcript with your own vessel details, plus drill the Alfa–Zulu code-words.",
    ready: true,
  },
  {
    slug: "chartwork",
    name: "Chartwork symbols",
    description: "Position fix, DR, EP, waypoint, water / ground / tidal vectors, and Course-to-Steer — the symbols you'll plot on a paper chart.",
    ready: true,
  },
  {
    slug: "soundings",
    name: "Soundings — hidden fleet",
    description: "Chart-search game. Find the hidden fleet, avoid the rocks. IALA cardinal marks point the way to safe water — deduce hazards from the marks and don't run aground.",
    ready: true,
  },
  {
    slug: "exam",
    name: "Mixed exam mode",
    description: "20 random questions drawn across every flashcards module — Morse, semaphore, flags, lights, day shapes, buoyage, distress, chartwork, weather, NATO. Per-topic breakdown at the end.",
    ready: true,
  },
  {
    slug: "buoyage",
    name: "IALA buoyage",
    description: "Cardinal, lateral, isolated-danger, safe-water, special and emergency-wreck marks. Region A (Europe etc.) + Region B (Americas etc.) — lateral marks invert between regions.",
    ready: true,
  },
  {
    slug: "day-shapes",
    name: "Day shapes",
    description: "Daytime black-silhouette signals — anchor ball, RAM ball-diamond-ball, draft cylinder, fishing diamond, aground three balls.",
    ready: true,
  },
  {
    slug: "irpcs",
    name: "IRPCS rules",
    description: "Animated scenarios — crossing, overtaking, head-on, sail-meets-power, fishing + NUC priority. Watch the geometry, pick the action.",
    ready: true,
  },
  {
    slug: "astro",
    name: "Astronavigation",
    description: "Celestial navigation — the Yachtmaster Ocean syllabus. Sextant corrections, the celestial sphere, Hs → Ho and (in the Reference tab) a live Sun ephemeris. Sight reduction, star ID, twilight + Moon/planets to follow.",
    ready: true,
  },
  {
    slug: "sailing",
    name: "Sailing — parts + points",
    description: "The vocabulary every sailor needs: the named parts of a Bermudan rig (head, tack, clew, luff, leech, foot, batten, telltale, reef points, cringle, kicker, outhaul, halyard, sheet, mast, boom) and the six points of sail relative to the wind. Reference diagrams in the Reference tab; drills here.",
    ready: true,
  },
];

/// Hub page listing every module. Each module that's `ready` links to
/// its own route (`/play/<slug>`); the rest render as "coming soon"
/// placeholders so the user can see the roadmap.
export function Play({ auth: _auth, onNavigate }: PlayProps) {
  return (
    <>
      <p className="muted">
        Pick a module. Each opens a demo + the game modes for that topic.
      </p>
      {MODULES.map((m) => (
        <div key={m.slug} className="card">
          <h2 style={{ marginBottom: 4 }}>
            {m.name}
            {!m.ready && (
              <span className="pill" style={{ marginLeft: 8, fontSize: 12 }}>
                coming soon
              </span>
            )}
          </h2>
          <p className="small muted" style={{ marginBottom: 12 }}>
            {m.description}
          </p>
          {m.ready ? (
            <button
              onClick={() => onNavigate(`/play/${m.slug}`)}
              className="secondary"
            >
              Open
            </button>
          ) : (
            <button disabled className="secondary">
              Not yet
            </button>
          )}
        </div>
      ))}
      <p
        className="small muted"
        style={{ marginTop: 24, marginBottom: 0 }}
      >
        Looking for the lookup reference or multi-player modes?
        They live in the navigation bar at the top.
      </p>
    </>
  );
}
